import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { getMonthOptions, currentMonth, getDateRangeForMonth, formatNum } from '../../lib/month';
import { useMdr } from '../../hooks/useMdr';

interface GroupItem {
  id: string;
  name: string;
  merchantIds: string[];
}

interface MerchantItem {
  merchantId: string;
  code: string;
  name: string;
  lastStatSyncAt: string | null;
}

interface SubShare {
  subMdrId: string;
  subMdrName: string;
  payinFeePercent: number;
  payoutFeePercent: number;
  payinAmount: number;
  payoutAmount: number;
}

interface Sharing {
  merchantId: string;
  merchantName: string;
  payinVolume: number;
  payoutVolume: number;
  masterPayinFeeAmount: number;
  masterPayoutFeeAmount: number;
  subShares: SubShare[];
}

interface StatsResponse {
  sharing: Sharing | null;
}

export function MdrGroupDetail() {
  const { t } = useTranslation();
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMaster } = useMdr();

  const [month, setMonth] = useState(currentMonth());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const monthOptions = getMonthOptions();

  const toggleCollapse = (sid: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  const { dateFrom, dateTo } = getDateRangeForMonth(month);
  const syncDate = new Date(`${month}-01T00:00:00.000+07:00`).toISOString();

  const { data: groupsData } = useQuery({
    queryKey: ['mdr', 'groups'],
    queryFn: () => apiGet<{ items: GroupItem[] }>('/mdr/groups'),
  });
  const { data: merchantsData } = useQuery({
    queryKey: ['mdr', 'merchants'],
    queryFn: () => apiGet<{ items: MerchantItem[] }>('/mdr/merchants'),
  });

  const group = groupsData?.ok ? groupsData.data.items.find((g) => g.id === groupId) : undefined;
  const allMerchants = merchantsData?.ok ? merchantsData.data.items : [];
  const groupMerchantIds = group?.merchantIds ?? [];
  const merchants = allMerchants.filter((m) => groupMerchantIds.includes(m.merchantId));
  const merchantIds = merchants.map((m) => m.merchantId);

  const { data: sharingMap, isFetching } = useQuery({
    queryKey: ['mdr', 'group-sharing', groupId, month, merchantIds.join(',')],
    enabled: merchants.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        merchants.map(async (m): Promise<[string, Sharing | null]> => {
          const res = await apiGet<StatsResponse>(`/mdr/merchants/${m.merchantId}/stats`, {
            dateFrom,
            dateTo,
            granularity: 'daily',
            pageSize: '100',
          });
          return [m.merchantId, res.ok ? res.data.sharing : null];
        }),
      );
      return Object.fromEntries(entries) as Record<string, Sharing | null>;
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['mdr', 'group-sharing', groupId] });

  const syncOne = useMutation({
    mutationFn: (merchantId: string) =>
      apiPost(`/mdr/merchants/${merchantId}/sync`, { queryParams: { date: syncDate } }),
    onSuccess: (res) => {
      if (res.ok) {
        invalidate();
        queryClient.invalidateQueries({ queryKey: ['mdr', 'merchants'] });
      }
    },
  });

  const syncAll = useMutation({
    mutationFn: async () => {
      await Promise.all(
        merchants.map((m) =>
          apiPost(`/mdr/merchants/${m.merchantId}/sync`, { queryParams: { date: syncDate } }),
        ),
      );
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['mdr', 'merchants'] });
    },
  });

  const show = (v: string) => (isFetching ? '…' : v);
  const codeByMerchant = new Map(merchants.map((m) => [m.merchantId, m.code]));

  // Per-merchant rows for the overview table.
  const rows = merchants.map((m) => {
    const s = sharingMap?.[m.merchantId] ?? null;
    const ownFee = (key: 'payinAmount' | 'payoutAmount') =>
      s ? s.subShares.reduce((acc, x) => acc + x[key], 0) : 0;
    return {
      m,
      payinVolume: s?.payinVolume ?? 0,
      payoutVolume: s?.payoutVolume ?? 0,
      payinFee: isMaster ? (s?.masterPayinFeeAmount ?? 0) : ownFee('payinAmount'),
      payoutFee: isMaster ? (s?.masterPayoutFeeAmount ?? 0) : ownFee('payoutAmount'),
    };
  });

  // Group sub shares by sub MDR, with per-merchant rows + totals.
  interface SubRow {
    merchant: MerchantItem;
    payinFeePercent: number;
    payoutFeePercent: number;
    payinAmount: number;
    payoutAmount: number;
  }
  const subGroups = (() => {
    const map = new Map<
      string,
      { name: string; rows: SubRow[]; totalPayin: number; totalPayout: number }
    >();
    for (const m of merchants) {
      const s = sharingMap?.[m.merchantId];
      if (!s) continue;
      for (const sub of s.subShares) {
        const g = map.get(sub.subMdrId) ?? {
          name: sub.subMdrName,
          rows: [],
          totalPayin: 0,
          totalPayout: 0,
        };
        g.rows.push({
          merchant: m,
          payinFeePercent: sub.payinFeePercent,
          payoutFeePercent: sub.payoutFeePercent,
          payinAmount: sub.payinAmount,
          payoutAmount: sub.payoutAmount,
        });
        g.totalPayin += sub.payinAmount;
        g.totalPayout += sub.payoutAmount;
        map.set(sub.subMdrId, g);
      }
    }
    return [...map.entries()]
      .map(([sid, g]) => ({ sid, ...g }))
      .sort((a, b) => a.name.localeCompare(b.name));
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/mdr/groups')}>
            ← {t('mdr.groupsTitle')}
          </Button>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {group ? group.name : t('mdr.groupsTitle')}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {isMaster && merchants.length > 0 && (
            <Button size="sm" onClick={() => syncAll.mutate()} disabled={syncAll.isPending}>
              {syncAll.isPending ? t('common.loading') : t('mdr.syncAll')}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>{t('mdr.merchantsOverview')}</CardTitle>
            <span className="text-xs font-medium text-gray-400">
              {dateFrom} ~ {dateTo}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {merchants.length === 0 ? (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('merchants.name')}</TableHead>
                    <TableHead className="text-right">{t('mdr.payinVolume')}</TableHead>
                    <TableHead className="text-right">{t('mdr.payoutVolume')}</TableHead>
                    <TableHead className="text-right">{t('transactions.calcPayinFee')}</TableHead>
                    <TableHead className="text-right">{t('transactions.calcPayoutFee')}</TableHead>
                    {isMaster && (
                      <TableHead className="text-right">{t('common.actions')}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(({ m, payinVolume, payoutVolume, payinFee, payoutFee }) => (
                    <TableRow key={m.merchantId}>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => navigate(`/mdr/merchants/${m.merchantId}`)}
                          className="text-left font-medium text-gray-900 hover:text-primary dark:text-gray-100"
                        >
                          {m.name}
                          <span className="ml-1.5 font-mono text-xs text-gray-400">{m.code}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-right">{show(formatNum(payinVolume))}</TableCell>
                      <TableCell className="text-right">{show(formatNum(payoutVolume))}</TableCell>
                      <TableCell className="text-right text-primary">
                        {show(formatNum(payinFee))}
                      </TableCell>
                      <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
                        {show(formatNum(payoutFee))}
                      </TableCell>
                      {isMaster && (
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => syncOne.mutate(m.merchantId)}
                            disabled={syncOne.isPending && syncOne.variables === m.merchantId}
                          >
                            {syncOne.isPending && syncOne.variables === m.merchantId
                              ? t('common.loading')
                              : t('admin.syncNow')}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('mdr.subSharingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 p-0 pb-2">
          {subGroups.length === 0 ? (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          ) : (
            subGroups.map((grp) => (
              <div key={grp.sid}>
                <button
                  type="button"
                  onClick={() => toggleCollapse(grp.sid)}
                  className="flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/70 px-5 py-2.5 text-left transition-colors hover:bg-gray-100 dark:border-gray-700/60 dark:bg-gray-900/30 dark:hover:bg-gray-800/60"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                    <span className="text-gray-400">{collapsed.has(grp.sid) ? '▸' : '▾'}</span>
                    {grp.name}
                  </span>
                  <span className="flex gap-4 text-xs">
                    <span className="text-primary">
                      {t('transactions.calcPayinFee')}: {show(formatNum(grp.totalPayin))}
                    </span>
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {t('transactions.calcPayoutFee')}: {show(formatNum(grp.totalPayout))}
                    </span>
                  </span>
                </button>
                {!collapsed.has(grp.sid) && (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('merchants.name')}</TableHead>
                          <TableHead className="text-right">{t('mdr.payinFee')}</TableHead>
                          <TableHead className="text-right">
                            {t('transactions.calcPayinFee')}
                          </TableHead>
                          <TableHead className="text-right">{t('mdr.payoutFee')}</TableHead>
                          <TableHead className="text-right">
                            {t('transactions.calcPayoutFee')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {grp.rows.map((r) => (
                          <TableRow key={`${grp.sid}-${r.merchant.merchantId}`}>
                            <TableCell>
                              {r.merchant.name}
                              <span className="ml-1.5 font-mono text-xs text-gray-400">
                                {codeByMerchant.get(r.merchant.merchantId)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">{r.payinFeePercent}%</TableCell>
                            <TableCell className="text-right text-primary">
                              {show(formatNum(r.payinAmount))}
                            </TableCell>
                            <TableCell className="text-right">{r.payoutFeePercent}%</TableCell>
                            <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
                              {show(formatNum(r.payoutAmount))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <tfoot className="border-t-2 border-gray-200 font-semibold dark:border-gray-600">
                        <TableRow>
                          <TableCell className="text-gray-900 dark:text-gray-100">
                            {t('transactions.total')}
                          </TableCell>
                          <TableCell>{null}</TableCell>
                          <TableCell className="text-right text-primary">
                            {show(formatNum(grp.totalPayin))}
                          </TableCell>
                          <TableCell>{null}</TableCell>
                          <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
                            {show(formatNum(grp.totalPayout))}
                          </TableCell>
                        </TableRow>
                      </tfoot>
                    </Table>
                  </div>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
